// TOUR-4a — fly the tour firefly between step anchors (reuses chase physics).

import { useCallback, useEffect, useRef, useState } from "react";
import { stepFireflyChaseMotion } from "@/components/activity/firefly/fireflyChaseMotion";

export type FireflyTourTravelPoint = { x: number; y: number };

export function useFireflyTourTravel(reducedMotion: boolean) {
  const [traveling, setTraveling] = useState(false);
  const [position, setPosition] = useState<FireflyTourTravelPoint>({ x: 0, y: 0 });
  const motionRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 });
  const rafRef = useRef<number | null>(null);
  const pendingResolveRef = useRef<(() => void) | null>(null);

  const cancelTravel = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setTraveling(false);
    // Resolve any in-flight flyTo so callers (sidebar dock travel host)
    // always clear `dockTravelActive` — hung promises hide the floating tab.
    const resolve = pendingResolveRef.current;
    pendingResolveRef.current = null;
    resolve?.();
  }, []);

  useEffect(() => cancelTravel, [cancelTravel]);

  const flyTo = useCallback(
    (from: FireflyTourTravelPoint, to: FireflyTourTravelPoint): Promise<void> => {
      cancelTravel();

      if (reducedMotion) {
        setPosition(to);
        return Promise.resolve();
      }

      const dist = Math.hypot(to.x - from.x, to.y - from.y);
      if (dist < 4) {
        setPosition(to);
        return Promise.resolve();
      }

      motionRef.current = { x: from.x, y: from.y, vx: 0, vy: 0 };
      setPosition(from);
      setTraveling(true);

      return new Promise((resolve) => {
        pendingResolveRef.current = resolve;
        const finish = () => {
          rafRef.current = null;
          setTraveling(false);
          pendingResolveRef.current = null;
          resolve();
        };
        const startedAt = performance.now();
        const tick = () => {
          // Hard stop — never leave travel hanging (disappearing Firefly).
          if (performance.now() - startedAt > 4000) {
            setPosition(to);
            finish();
            return;
          }
          const next = stepFireflyChaseMotion(motionRef.current, to, "settle");
          motionRef.current = { x: next.x, y: next.y, vx: next.vx, vy: next.vy };
          setPosition({ x: next.x, y: next.y });
          if (next.arrived) {
            finish();
            return;
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      });
    },
    [cancelTravel, reducedMotion],
  );

  return {
    traveling,
    position,
    flyTo,
    cancelTravel,
  };
}
